import { AxiosError } from "axios";
import {
  Button,
  CheckBox,
  LoadIndicator,
  Popup,
  TagBox,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { EmailRule, RequiredRule, StringLengthRule } from "devextreme-react/validator";
import { ClickEvent } from "devextreme/ui/button";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ServerResponse from "../../components/ServerResponse";
import { RoleLookupDS } from "../../dataSources/RoleLookupDS";
import { closePopup, reloadGrid } from "../../helper/DxHelper";
import { useUserUpdate } from "../../hooks/useUserUpdate";
import { SystemRole, DataAwareResponse, User, ValidationResultItem } from "../../types/types";
import { axiosInstance } from "@/helper/axios-instance";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { useAppSelector } from "@/hooks/useAppSelector";
import { TextBoxRef } from "devextreme-react/cjs/text-box";
import { CheckBoxRef } from "devextreme-react/cjs/check-box";
import { TagBoxRef } from "devextreme-react/cjs/tag-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<User, string>>;
};

const validationGroupName = "updateUserVal";

export default function Update({ popupRef, datagridRef }: Props) {
  const userId = useAppSelector((state) => state.user.editUserId);
  const userName = useAppSelector((state) => state.user.editUserFullname);
  const [userError, setUserError] = useState("");
  const firstNameRef = useRef<TextBoxRef>(null);
  const lastNameRef = useRef<TextBoxRef>(null);
  const emailRef = useRef<TextBoxRef>(null);
  const permissionsRef = useRef<TagBoxRef>(null);
  const usernameRef = useRef<TextBoxRef>(null);
  const inactiveRef = useRef<CheckBoxRef>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useUserUpdate();
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const [anyChange, setAnyChange] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (userId) {
      hydrateForm();
    }
  }, [userId]);

  function submit(e: ClickEvent) {
    setServerErrors(null);
    setAnyChange(false);

    if (!e.validationGroup.validate().isValid || !userId) return;

    const roles = permissionsRef?.current?.instance().option("value");
    const newRoles: Array<string> = [];
    if (roles[0]?.name) {
      roles.forEach((item: SystemRole) => {
        newRoles.push(item.id);
      });
    }

    const requestData = {
      id: userId,
      firstName: firstNameRef?.current?.instance()?.option("value"),
      lastName: lastNameRef?.current?.instance()?.option("value"),
      email: emailRef?.current?.instance()?.option("value"),
      username: usernameRef?.current?.instance()?.option("value"),
      inactive: inactiveRef?.current?.instance()?.option("value"),
      permissions: newRoles?.length ? newRoles : roles,
    };

    mutate(requestData);
  }

  function setDisableInputs(state: boolean) {
    firstNameRef.current?.instance().option("disabled", state);
    lastNameRef.current?.instance().option("disabled", state);
    emailRef.current?.instance().option("disabled", state);
    permissionsRef.current?.instance().option("disabled", state);
    usernameRef.current?.instance().option("disabled", state);
  }

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        toast.success(
          <div>
            <span>User {`${response.data.firstName} ${response.data.lastName}`} updated</span>
          </div>,
          {
            type: "success",
            autoClose: 3000,
            closeButton: true,
            closeOnClick: true,
          }
        );

        closePopup(popupRef);

        if (!response?.data?.roles.find((role) => role.name.toLowerCase() === "administrator")) {
          navigate("/household");
          qc.invalidateQueries();
        }
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    setDisableInputs(isPending);
  }, [isPending]);

  async function fetchAndSetUser() {
    try {
      const response = await axiosInstance({
        method: "GET",
        url: `SystemUser/Get?id=${userId}`,
      });
      const dataResponse = response.data as DataAwareResponse<User>;

      firstNameRef.current?.instance().option("value", dataResponse.data.firstName);
      lastNameRef.current?.instance().option("value", dataResponse.data.lastName);
      emailRef.current?.instance().option("value", dataResponse.data.email);
      usernameRef.current?.instance().option("value", dataResponse.data.username);
      permissionsRef?.current?.instance()?.option("value", dataResponse.data.roles);
      inactiveRef?.current?.instance()?.option("value", dataResponse.data.inactive);
    } catch (error) {
      if (error instanceof AxiosError) setUserError(error.message);
    }

    setAnyChange(false);
  }

  function hydrateForm() {
    fetchAndSetUser();
  }

  return (
    <Popup
      title={`Edit ${userName}`}
      ref={popupRef}
      showCloseButton={true}
      onHiding={() => {
        setServerErrors(null);
        setUserError("");
        reloadGrid(datagridRef);
      }}
    >
      <div>
        {userError ? (
          <span>{userError}</span>
        ) : (
          <>
            <form className="grid grid-cols-2 gap-8">
              <TextBox
                label="First Name*"
                labelMode="static"
                ref={firstNameRef}
                stylingMode="outlined"
                valueChangeEvent="keyup"
              >
                <Validator validationGroup={validationGroupName}>
                  <RequiredRule message={"First Name is required"} />
                  <StringLengthRule max={50} message={`Max length of first name is 50`} />
                </Validator>
              </TextBox>

              <TextBox
                label="Last Name*"
                labelMode="static"
                ref={lastNameRef}
                stylingMode="outlined"
                valueChangeEvent="keyup"
              >
                <Validator validationGroup={validationGroupName}>
                  <RequiredRule message={"Last Name is required"} />
                  <StringLengthRule max={50} message={`Max length of last name is 50`} />
                </Validator>
              </TextBox>
              <TextBox
                label="Email Address*"
                labelMode="static"
                ref={emailRef}
                stylingMode="outlined"
                valueChangeEvent="keyup"
              >
                <Validator validationGroup={validationGroupName}>
                  <RequiredRule message={"Email Address is required"} />
                  <EmailRule message={"Invalid email format"} />
                  <StringLengthRule max={128} message={`Max length of email address is 128`} />
                </Validator>
              </TextBox>
              <TextBox
                label="Username*"
                labelMode="static"
                ref={usernameRef}
                stylingMode="outlined"
              >
                <Validator validationGroup={validationGroupName}>
                  <RequiredRule message={"Username is required"} />
                  <StringLengthRule max={128} message={`Max length of Username is 128`} />
                </Validator>
              </TextBox>
              <TagBox
                label="Roles"
                labelMode="static"
                ref={permissionsRef}
                dataSource={RoleLookupDS()}
                displayExpr={"name"}
                stylingMode={"outlined"}
                showClearButton={true}
                showSelectionControls={true}
                searchEnabled={true}
                multiline={false}
                selectAllMode="page"
                onValueChanged={() => setAnyChange(true)}
              />

              <div className="mt-4 flex flex-col gap-2">
                <CheckBox ref={inactiveRef} text="Inactive" />
              </div>
            </form>
            <div className="absolute bottom-4 left-4">
              <div className="text-danger">
                {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
              </div>
              <ValidationSummary
                validationGroup={validationGroupName}
                id="summary"
              ></ValidationSummary>
              {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
            </div>
            <div className="absolute bottom-4 right-4">
              {anyChange && (
                <Button
                  text="reset"
                  onClick={hydrateForm}
                  useSubmitBehavior={false}
                  stylingMode="text"
                  className="mr-4"
                />
              )}
              <Button
                validationGroup={validationGroupName}
                useSubmitBehavior={false}
                type="default"
                height={40}
                width={isPending ? 150 : 100}
                onClick={submit}
              >
                <LoadIndicator
                  id="default-btn-loading"
                  className="mr-3 inline-block align-middle"
                  width={25}
                  height={25}
                  visible={isPending}
                />
                <span className="dx-button-text">{isPending ? "Updating" : "Update"}</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </Popup>
  );
}
